import { useUser } from '@/hooks/user'
import { match } from 'ts-pattern'
import { Toaster } from '@/components/ui/toaster'
import { UpdatePrompt } from './UpdatePrompt'
import { Button } from '@/components/ui/button'
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuTrigger,
  NavigationMenuContent,
  NavigationMenuLink,
} from '@/components/ui/navigation-menu'
import Topbar from '@/components/Topbar'
import { Router, matchRouteConfig, useAllRoutes } from '@/router'
import type { PropsWithChildren } from 'react'
import { Link } from '@swan-io/chicane'
import { RestTimer } from './RestTimer'
import { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
import { NotFound } from '@/pages/NotFound'
import { LayoutDashboard as HomeIcon } from 'lucide-react'
import { ThemeToggle } from '@/components/ThemeToggle'

type Props = PropsWithChildren<{
  viewportRef?: React.RefObject<HTMLElement>
}>
export function AppShell({ children, viewportRef }: Props) {
  const isLoggedIn = useUser((c) => !!c.userId)
  const route = useAllRoutes()
  const routeConfig = matchRouteConfig(route)

  return (
    <>
      <Topbar routeConfig={routeConfig} />
      <main
        id="screen"
        ref={viewportRef}
        className="[grid-area:viewport] overflow-y-auto bg-grid text-stone-700 dark:text-stone-300 flex flex-1 margin-0"
      >
        <SentryErrorBoundary
          fallback={
            <NotFound
              title="Unexpected Error"
              actionCallback={() => location.reload()}
              message="Sorry about that! Try refreshing the page."
            />
          }
        >
          {children}
        </SentryErrorBoundary>
      </main>
      <RestTimer className="[grid-area:utilbar]" />
      <nav className="[grid-area:navbar] flex bg-emerald-500 text-emerald-100 dark:bg-stone-900 dark:text-stone-500">
        <ul className="flex w-content gap-8 justify-between items-center list-none p-4">
          <li>
            <Button asChild>
              <Link className="!px-2" to={Router.Root()}>
                <HomeIcon className="w-6 h-6" />
              </Link>
            </Button>
          </li>
          <NavigationMenu orientation="vertical" className="z-0">
            <NavigationMenuList>
              <NavigationMenuItem>
                <Button asChild>
                  <NavigationMenuTrigger>Setup</NavigationMenuTrigger>
                </Button>
                <NavigationMenuContent>
                  <ul className="w-full vstack !gap-0 divide-y divide-emerald-50 dark:divide-stone-700">
                    <NavMenuLinkItem to={Router.StatsIndex()}>
                      Stats
                    </NavMenuLinkItem>
                    <NavMenuLinkItem to={Router.ProgressionIndex()}>
                      Progressions
                    </NavMenuLinkItem>
                    <NavMenuLinkItem to={Router.ExerciseIndex()}>
                      Exercises
                    </NavMenuLinkItem>
                    <NavMenuLinkItem to={Router.ActivityIndex()}>
                      Activities
                    </NavMenuLinkItem>
                    {/* <NavMenuLinkItem to={Router.Quickadd()}>Quickadd</NavMenuLinkItem> */}
                    <NavMenuLinkItem to={Router.ProgramIndex()}>
                      Programs
                    </NavMenuLinkItem>
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          <ThemeToggle />
          {match(isLoggedIn)
            .with(true, () => {
              return (
                <>
                  <li>
                    <Link to={Router.User()} className="font-medium">
                      User
                    </Link>
                  </li>
                </>
              )
            })
            .otherwise(() => {
              return (
                <li>
                  <Link to={Router.User()}>Sign In</Link>
                </li>
              )
            })}
        </ul>
      </nav>
      <Toaster />
      <UpdatePrompt />
    </>
  )
}

function NavMenuLinkItem({
  to,
  children,
}: {
  to: string
  children: React.ReactNode
}) {
  return (
    <li className="w-full">
      <NavigationMenuLink asChild>
        <Link
          to={to}
          activeClassName="bg-emerald-300 text-emerald-50 hover:text-emerald-700"
          className="p-4 font-sans flex w-full hover:bg-emerald-100 hover:text-emerald-700 transition-colors duration-250"
        >
          {children}
        </Link>
      </NavigationMenuLink>
    </li>
  )
}
